import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { 
  Bell, Check, MessageSquare, Users, Shield, Megaphone, 
  AlertTriangle, TrendingUp, Gift, DollarSign, CreditCard, Sparkles 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NOTIFICATION_ICONS = {
  message: MessageSquare,
  broadcast: Megaphone,
  member_joined: Users,
  role_change: Shield,
  invite: Users,
  limit_warning: TrendingUp,
  limit_reached: AlertTriangle,
  collaborator_approved: Gift,
  collaborator_rejected: Shield,
  collaborator_earning: DollarSign,
  collaborator_payout: DollarSign,
  subscription_upgraded: Sparkles,
  credits_purchased: CreditCard,
  weekly_digest: TrendingUp
};

const NOTIFICATION_COLORS = {
  limit_warning: 'text-amber-500',
  limit_reached: 'text-red-500',
  collaborator_approved: 'text-green-500',
  collaborator_rejected: 'text-gray-500',
  collaborator_earning: 'text-green-500',
  collaborator_payout: 'text-green-500',
  subscription_upgraded: 'text-purple-500',
  credits_purchased: 'text-blue-500',
  weekly_digest: 'text-blue-500'
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, []);

  useEffect(() => {
    // Initial fetch with slight delay to avoid cascading render
    const initialFetch = setTimeout(fetchNotifications, 100);
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => {
      clearTimeout(initialFetch);
      clearInterval(interval);
    };
  }, [fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, { withCredentials: true });
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="notification-bell"
        >
          <Bell className="w-5 h-5" />
          <AnimatePresence>
            {unreadCount > 0 && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium"
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </motion.span>
            )}
          </AnimatePresence>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold">Notifications</h3>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" onClick={markAllAsRead} className="text-xs">
              Mark all read
            </Button>
          )}
        </div>
        
        <ScrollArea className="h-[300px]">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
              <Bell className="w-8 h-8 mb-2 opacity-50" />
              <p className="text-sm">No notifications</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notif) => {
                const Icon = NOTIFICATION_ICONS[notif.type] || Bell;
                const colorClass = NOTIFICATION_COLORS[notif.type] || 'text-slate-600';
                
                const handleClick = () => {
                  if (!notif.read) {
                    markAsRead(notif.notification_id);
                  }
                  // Navigate to action URL if available
                  if (notif.action_url) {
                    setIsOpen(false);
                    navigate(notif.action_url);
                  }
                };
                
                return (
                  <div
                    key={notif.notification_id}
                    className={`p-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors ${
                      !notif.read ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''
                    }`}
                    onClick={handleClick}
                  >
                    <div className="flex gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        notif.type === 'limit_reached' ? 'bg-red-100' :
                        notif.type === 'limit_warning' ? 'bg-amber-100' :
                        notif.type === 'collaborator_approved' ? 'bg-green-100' :
                        notif.type === 'collaborator_earning' ? 'bg-green-100' :
                        notif.type === 'subscription_upgraded' ? 'bg-purple-100' :
                        notif.type === 'credits_purchased' ? 'bg-blue-100' :
                        notif.type === 'broadcast' ? 'bg-amber-100' :
                        notif.type === 'member_joined' ? 'bg-green-100' :
                        'bg-slate-100'
                      } ${colorClass}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{notif.title}</p>
                        <p className="text-xs text-muted-foreground line-clamp-2">{notif.message || notif.content}</p>
                        <p className="text-xs text-muted-foreground mt-1">{formatTime(notif.created_at)}</p>
                      </div>
                      {!notif.read && (
                        <div className="w-2 h-2 bg-amber-500 rounded-full flex-shrink-0 mt-2" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
